// Customers page component
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  TablePagination,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Search,
  Business,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { useCompanies } from '../hooks/useCompanies';
import { STATUS_COLORS } from '../helpers/constants';
import { 
  getTableHeaderStyles, 
  getTableContainerStyles, 
  getTableStyles,
  getTableCellStyles,
  getTableRowStyles,
  formatStatusText
} from '../styles/tableStyles';

import { formatTableDate } from '../utils/dateUtils';

const Customers: React.FC = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const { companies, loading, error } = useCompanies();
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  


  // Filter companies based on search term (backend now excludes root company)
  const filteredCompanies = companies.filter(company =>
    company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    company.domain.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (company.status && company.status.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (company.industry && company.industry.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Get current page data
  const currentCompanies = filteredCompanies.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status: string | undefined) => {
    if (!status) return 'default';
    const statusKey = status.toUpperCase() as keyof typeof STATUS_COLORS;
    return STATUS_COLORS[statusKey] || 'default';
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Header with search (page title shown in Layout toolbar) */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-start', 
        alignItems: 'center', 
        mb: 2,
        gap: 2
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1
        }}>
          <TextField
            size="small"
            placeholder="Search customers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            sx={{ 
              width: 200,
              '& .MuiOutlinedInput-root': {
                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                '&:hover': {
                  backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                },
                '&.Mui-focused': {
                  backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.06)',
                },
              },
            }}
          />
        </Box>
      </Box>

      {/* Customer table */}
      <Paper sx={getTableContainerStyles()}>
        <TableContainer>
          <Table {...getTableStyles()}>
            <TableHead>
              <TableRow sx={getTableRowStyles()}>
                <TableCell sx={getTableHeaderStyles(isDarkMode)}>
                  Company
                </TableCell>
                <TableCell sx={getTableHeaderStyles(isDarkMode)}>
                  Domain
                </TableCell>
                <TableCell sx={getTableHeaderStyles(isDarkMode)}>
                  Status
                </TableCell>
                <TableCell sx={getTableHeaderStyles(isDarkMode)}>
                  Industry
                </TableCell>
                <TableCell sx={getTableHeaderStyles(isDarkMode)}>
                  Plan
                </TableCell>
                <TableCell sx={getTableHeaderStyles(isDarkMode)}>
                  Created
                </TableCell>
                <TableCell sx={getTableHeaderStyles(isDarkMode)}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow sx={getTableRowStyles()}>
                  <TableCell colSpan={7} align="center" sx={getTableCellStyles()}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                      <CircularProgress />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow sx={getTableRowStyles()}>
                  <TableCell colSpan={7} align="center" sx={getTableCellStyles()}>
                    <Alert severity="error" sx={{ my: 2 }}>
                      {error}
                    </Alert>
                  </TableCell>
                </TableRow>
              ) : currentCompanies.length === 0 ? (
                <TableRow sx={getTableRowStyles()}>
                  <TableCell colSpan={7} align="center" sx={getTableCellStyles()}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      No companies found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                currentCompanies.map((company) => (
                  <TableRow
                    key={company.id}
                    sx={getTableRowStyles()}
                  >
                    <TableCell sx={getTableCellStyles()}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Business sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography
                          component="span"
                          sx={{
                            color: 'primary.main',
                            cursor: 'pointer',
                            '&:hover': {
                              color: 'primary.dark',
                            },
                          }}
                          onClick={() => navigate(`/customers/${company.id}`)}
                        >
                          {company.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    {/* Domain with email/domain icon (matches profile dropdown style) */}
                    <TableCell sx={getTableCellStyles()}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2">
                          {company.domain}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={getTableCellStyles()}>
                      <Chip 
                        label={formatStatusText(company.status)} 
                        color={getStatusColor(company.status || 'INACTIVE') as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={getTableCellStyles()}>
                      <Typography variant="body2">
                        {company.industry || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={getTableCellStyles()}>
                      <Typography variant="body2">
                        {company.planName || 'Trial Plan'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={getTableCellStyles()}>
                      <Typography variant="body2">
                        {company.createdAt ? formatTableDate(company.createdAt, 'created') : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={getTableCellStyles()}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => navigate(`/customers/${company.id}`)}
                        sx={{
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          padding: '2px 8px',
                        }}
                      >
                        Manage
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredCompanies.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

    </Box>
  );
};

export default Customers; 